/**
 * Pure line-drafting helpers for LineItemsEditor.jsx's Add Goods/Service
 * Line split button (supply-and-install case, decided 2026-07-21). Kept
 * framework-free so the seeding and per-row date-field selection can be
 * unit-tested without a component-rendering library (none is installed in
 * this project - see lineItemLocationChecks.test.js for the same pattern).
 */
import { effectiveItemType } from './lineItemValidation';

// Which date field(s) a row should render, driven by the line's own
// effective type - not the doc-level procurementType (a service line in a
// goods procurement must show Start/End, not Need By Date, and vice versa).
export const dateFieldsForItemType = (itemType) =>
  itemType === 'services' ? ['start_date', 'end_date'] : ['need_by_date'];

// Composition of the above with effectiveItemType - the exact rule
// LineItemsEditor.jsx applies per row when deciding which date picker(s)
// to render.
export const dateFieldsForLine = (item, procurementType) =>
  dateFieldsForItemType(effectiveItemType(item, procurementType));

// Seeds a new user-added line item for the given explicit type. item_type
// is always stamped so effectiveItemType's rule 1 (line's own type wins)
// classifies it correctly regardless of the document's procurementType -
// type is fixed at creation (no per-row toggle; delete and re-add to
// change it).
export const createLineItem = ({ type, lineNumber, shipToLocation = '' }) => {
  const isServiceLine = type === 'services';
  return {
    line_number: lineNumber,
    description: '',
    quantity: 1,
    uom: 'Each',
    need_by_date: isServiceLine ? null : '',
    start_date: isServiceLine ? '' : null,
    end_date: isServiceLine ? '' : null,
    ship_to_location: shipToLocation || '',
    spend_category: '',
    item_type: type,
    // EXECUTION SCHEDULE MODEL (Ashok's design, 2026-07-26): a manually
    // added service line always starts BUYER_DEFINED - VENDOR_PROPOSED is
    // only ever set by the refinement agent when the buyer left the
    // execution-window question unanswered. The Lines editor never offers
    // a "mark as vendor-proposed" toggle, only the one-way flip back to
    // BUYER_DEFINED when a date is entered on an existing VENDOR_PROPOSED
    // line (see LineItemsEditor.jsx's updateLineItem).
    schedule_mode: isServiceLine ? 'BUYER_DEFINED' : undefined,
    // Provenance (spec P2 §5/§10.3): a manually added line is user-authored.
    source: 'user',
  };
};
